import { useCallback } from 'react';

import { useAIAnswerContext } from '../../../../ai-answer-dialog/ai-answer-context';
import { createAtlAttributionHeader } from '../../../../common/fetch/atl-attribution-header';
import { fetchAggResponse } from '../../../../common/fetch/fetch-agg-response';
import { fetchRestResponse } from '../../../../common/fetch/fetch-rest-reponse';
import {
	SearchSourceQuery,
	type SearchSourceQueryType,
	type SearchSourceQueryVariables,
} from '../../gql/search-source-query';
import type {
	CuratedDefinitionSearchSourcesFunctionType,
	ObjectResolverDataType,
	ObjectResolverParamType,
} from '../types';

import { getQuerySpecsPerEntity } from './get-query-specs-per-entity';

type SearchSourceSupplierType = {
	searchSources: CuratedDefinitionSearchSourcesFunctionType;
};

const OBJECT_RESOLVE_ENDPOINT = '/gateway/api/object-resolver/resolve/batch';

export const useSearchSourceSupplier = (): SearchSourceSupplierType => {
	const { commonAttributes, sourceProduct } = useAIAnswerContext();
	const { cloudId, extraAttributes, workspaceId } = commonAttributes;
	const { readingAidsSessionId } = extraAttributes;

	const searchSources = useCallback(
		async (query: string) => {
			const { searchQuery, entities, spacesFilter, needsLinkResolution } =
				getQuerySpecsPerEntity(query);
			let details;
			if (needsLinkResolution) {
				details = await fetchRestResponse<ObjectResolverParamType[], ObjectResolverDataType[]>({
					url: OBJECT_RESOLVE_ENDPOINT,
					body: [{ resourceUrl: query.split('?')[0] }],
				});
			}
			return fetchAggResponse<SearchSourceQueryVariables, SearchSourceQueryType>({
				graphQuery: SearchSourceQuery,
				variables: {
					experience: 'curated-definition-source-search',
					analytics: {
						searchSessionId: readingAidsSessionId as string,
					},
					query: details?.[0]?.body?.data?.name ?? searchQuery,
					filters: {
						entities: entities,
						locations: [cloudId ? `ari:cloud:confluence::site/${cloudId}` : ''],
						confluenceFilters: {
							titleMatchOnly: true,
							spacesFilter: spacesFilter,
						},
					},
					first: 5,
				},
				headers: createAtlAttributionHeader({
					atlWorkspaceId: workspaceId,
					tenantId: cloudId,
					product: sourceProduct ?? '',
				}),
			}).then((response) => {
				if (response.data) {
					return response.data.search.results.edges.map((edge) => ({
						id: edge.node?.id || '',
						type: edge.node?.type || '',
						title: edge.node?.title || '',
						container: edge.node?.space?.name || '',
						url: edge.node?.url || '',
						iconUrl: edge.node?.iconUrl || '',
						lastModified: edge.node?.lastModified || '',
					}));
				}
				return [];
			});
		},
		[cloudId, readingAidsSessionId, workspaceId, sourceProduct],
	);

	return { searchSources };
};
